import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import {
  cacheCounterItems,
  loadCachedCounterItems,
  enqueueSubmission,
  applyLocalOptimistic,
  flushQueue,
  readQueue,
  startBackgroundSync,
} from "../lib/offline";
import AppShell from "../components/AppShell";
import StatusBadge from "../components/StatusBadge";
import { toast, Toaster } from "sonner";
import {
  PaperPlaneRight,
  ClipboardText,
  MagnifyingGlass,
  Warehouse,
  ArrowLeft,
  Lock,
  Plus,
  WifiSlash,
} from "@phosphor-icons/react";

function CounterStatus({ item, isCutoff }) {
  const hasMyCount = item.my_latest_count != null;
  const submittedCount = item.reconciliation?.submitted_counter_count ?? null;

  if (!hasMyCount && submittedCount === 0) {
    return (
      <span className="inline-flex items-center rounded-sm border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
        No Count
      </span>
    );
  }

  if (isCutoff) {
    return (
      <span className="inline-flex items-center rounded-sm border border-zinc-400 bg-zinc-200 px-2 py-0.5 text-[11px] font-semibold text-zinc-700">
        Cutoff
      </span>
    );
  }

  return <StatusBadge status={item.ui_status} direction={item.discrepancy_sign} />;
}

export function CounterDashboard() {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/sessions")
      .then(({ data }) => setSessions(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const activeSessions = sessions.filter((s) => s.session_status === "active");

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-4xl mx-auto p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Counter</div>
          <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">My counting sessions</h1>
          <p className="text-sm text-zinc-600">Choose an active session to record your physical counts.</p>
        </div>

        <div className="grid gap-3">
          {activeSessions.map((s) => (
            <Link
              key={s.id}
              to={`/counter/sessions/${s.id}`}
              className="rounded-sm border border-zinc-300 bg-white p-4 flex items-center justify-between hover:border-indigo-400 hover:-translate-y-[1px] transition-all"
              data-testid={`counter-session-${s.id}`}
            >
              <div>
                <div className="font-display font-semibold text-zinc-900">{s.session_name}</div>
                <div className="text-xs text-zinc-500 mono">
                  {s.warehouse_name || "—"} · {s.item_count} SKUs {s.is_cutoff ? "· CUTOFF" : ""}
                </div>
              </div>
              <div className="inline-flex items-center gap-1 text-sm text-indigo-900">
                {s.is_cutoff ? (
                  <>
                    Counts locked <Lock className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    Start counting <ClipboardText className="w-4 h-4" />
                  </>
                )}
              </div>
            </Link>
          ))}

          {activeSessions.length === 0 && (
            <div className="rounded-sm border border-zinc-300 bg-white p-10 text-center text-zinc-500">
              No active sessions assigned to you right now.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export function CounterSession() {
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [submitVal, setSubmitVal] = useState("");
  const [remarksVal, setRemarksVal] = useState("");
  const [partnerVal, setPartnerVal] = useState("");
  const [partnerNames, setPartnerNames] = useState([]);
  const [locationVal, setLocationVal] = useState("");
  const [countHistory, setCountHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [queueCount, setQueueCount] = useState(readQueue().length);

  const [foundModal, setFoundModal] = useState(false);
  const [foundSaving, setFoundSaving] = useState(false);
  const [foundForm, setFoundForm] = useState({
    warehouse_code: "",
    item_code: "",
    item_description: "",
    size: "",
    location: "",
    tonality: "",
    pcs_per_box: "",
    price: "",
    counted_quantity: "",
    alley_supervisor: "",
    remarks: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/counter/sessions/${id}/items`);
      setData(data);
      cacheCounterItems(id, data);
    } catch (e) {
      const cached = loadCachedCounterItems(id);
      if (cached?.data) {
        setData(cached.data);
        toast.message("Offline — showing last saved view", {
          description: "Queued counts will sync when internet returns.",
        });
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const syncAndReload = async () => {
    const result = await flushQueue();
    setQueueCount(readQueue().length);
    if (result.synced > 0 || result.failed > 0) {
      toast.success(`Synced ${result.synced} queued count${result.synced === 1 ? "" : "s"}`);
      await load();
    }
  };

  const loadPartnerNames = async () => {
    try {
      const { data } = await api.get(`/counter/sessions/${id}/partner-names`);
      setPartnerNames(Array.isArray(data?.names) ? data.names : []);
    } catch {
      // Offline or first use — suggestions are optional, typing still works.
    }
  };

  useEffect(() => {
    startBackgroundSync();
    load();
    loadPartnerNames();
    syncAndReload().catch(() => {});

    const onQueue = () => setQueueCount(readQueue().length);
    const onOnline = () => syncAndReload().catch(() => {});

    window.addEventListener("irs:queue", onQueue);
    window.addEventListener("online", onOnline);

    const timer = setInterval(() => {
      syncAndReload().catch(() => {});
    }, 20000);

    return () => {
      window.removeEventListener("irs:queue", onQueue);
      window.removeEventListener("online", onOnline);
      clearInterval(timer);
    };
  }, [id]);

  const resetFoundForm = () => {
    setFoundForm({
      warehouse_code: "",
      item_code: "",
      item_description: "",
      size: "",
      location: "",
      tonality: "",
      pcs_per_box: "",
      price: "",
      counted_quantity: "",
      alley_supervisor: "",
      remarks: "",
    });
  };

  const closeCountModal = () => {
    if (submitting) return;

    setSelected(null);
    setSubmitVal("");
    setRemarksVal("");
    setPartnerVal("");
    setLocationVal("");
    setCountHistory([]);
    setHistoryLoading(false);
  };

  const openCountModal = async (item) => {
    setSelected(item);
    setSubmitVal(item.my_latest_count != null ? String(item.my_latest_count) : "");
    setRemarksVal(item.my_remarks || "");
    setPartnerVal(item.my_warehouse_partner_name || "");
    setLocationVal(item.my_submitted_location || item.location || "");
    setCountHistory([]);
    setHistoryLoading(true);

    try {
      const { data } = await api.get(
        `/counter/sessions/${id}/items/${item.id}/history`
      );
      setCountHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      if (navigator.onLine) {
        toast.error("Unable to load submitted count history");
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const submit = async () => {
    if (!selected) return;

    const raw = submitVal.trim();
    const n = raw === "" ? 0 : parseInt(raw, 10);
    if (isNaN(n) || n < 0) {
      toast.error("Enter a valid whole number");
      return;
    }

    const remarks = remarksVal.trim();
    const warehousePartnerName = partnerVal.trim();
    const submittedLocation = locationVal.trim();

    if (!warehousePartnerName) {
      toast.error("Enter the warehouse partner name");
      return;
    }

    if (!submittedLocation) {
      toast.error("Enter the current item location");
      return;
    }

    setSubmitting(true);

    try {
      if (!navigator.onLine) throw new Error("offline");

      await api.post(`/counter/sessions/${id}/items/${selected.id}/submit`, {
        counted_quantity: n,
        warehouse_partner_name: warehousePartnerName,
        submitted_location: submittedLocation,
        remarks,
      });

      toast.success("Count submitted");
      setSelected(null);
      setSubmitVal("");
      setRemarksVal("");
      setPartnerVal("");
      setLocationVal("");
      setCountHistory([]);
      await load();
      loadPartnerNames();
    } catch (e) {
      const offline = !navigator.onLine || e?.code === "ERR_NETWORK" || e?.message === "offline";
      if (offline) {
        enqueueSubmission({
          session_id: id,
          item_id: selected.id,
          counted_quantity: n,
          warehouse_partner_name: warehousePartnerName,
          submitted_location: submittedLocation,
          remarks,
          submitted_at: new Date().toISOString(),
        });

        applyLocalOptimistic(
          id,
          selected.id,
          n,
          remarks,
          warehousePartnerName,
          submittedLocation,
        );
        setQueueCount(readQueue().length);
        toast.success("Saved offline", {
          description: "This count is queued and will sync automatically.",
        });
        setSelected(null);
        setSubmitVal("");
        setRemarksVal("");
        setPartnerVal("");
        setLocationVal("");
        setCountHistory([]);
        const cached = loadCachedCounterItems(id);
        if (cached?.data) setData(cached.data);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const addFoundItem = async () => {
    const qty = parseInt(foundForm.counted_quantity || "0", 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Enter a valid found quantity");
      return;
    }

    if (!foundForm.item_code.trim() && !foundForm.item_description.trim()) {
      toast.error("Enter at least an item code or description");
      return;
    }

    setFoundSaving(true);
    try {
      await api.post(`/counter/sessions/${id}/found-item`, {
        ...foundForm,
        counted_quantity: qty,
        pcs_per_box: foundForm.pcs_per_box === "" ? null : Number(foundForm.pcs_per_box),
        price: foundForm.price === "" ? null : Number(foundForm.price),
      });

      toast.success("Found item added");
      setFoundModal(false);
      resetFoundForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFoundSaving(false);
    }
  };

  const isCutoff = !!data?.session?.is_cutoff;

  const items = useMemo(() => {
    const source = Array.isArray(data?.items) ? data.items : [];
    const search = q.trim().toLowerCase();

    return source.filter((it) => {
      const text = [
        it.item_code,
        it.item_description,
        it.warehouse_code,
        it.size,
        it.location,
        it.tonality,
        it.source,
        it.my_latest_count,
      ].map((v) => (v == null ? "" : String(v))).join(" ").toLowerCase();

      if (search && !text.includes(search)) return false;

      const noCount = (it.reconciliation?.submitted_counter_count ?? null) === 0 && it.my_latest_count == null;

      if (filter === "all") return true;
      if (filter === "no_count") return noCount;
      if (filter === "matched") return it.ui_status === "matched";
      if (filter === "locked") return it.ui_status === "locked";
      if (filter === "over_short") return it.ui_status === "recount_required";
      if (filter === "found") return it.source === "counter_added";
      return true;
    });
  }, [data, q, filter]);

  if (!data) {
    return (
      <AppShell>
        <Toaster position="top-right" richColors />
        <div className="p-10 text-zinc-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-[1600px] mx-auto p-3 sm:p-6 space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <Link to="/counter" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight mt-1">
              {data.session?.session_name || "Counting Session"}
            </h1>
            <div className="text-sm text-zinc-600 mono">
              <Warehouse className="w-4 h-4 inline mr-1 text-zinc-500" />
              {data.session?.warehouse_name || "—"}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {queueCount > 0 && (
              <button
                onClick={() => syncAndReload().catch(() => toast.error("Unable to sync now"))}
                className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
              >
                <WifiSlash className="w-4 h-4" /> {queueCount} pending sync
              </button>
            )}

            {isCutoff && (
              <div className="inline-flex items-center gap-2 rounded-sm border border-zinc-400 bg-zinc-200 px-3 py-1.5 text-sm text-zinc-800">
                <Lock className="w-4 h-4" /> Cutoff
              </div>
            )}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass className="w-4 h-4 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              placeholder="Search code, tonality, warehouse, location…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full rounded-sm border border-zinc-300 pl-8 pr-3 py-2 text-sm"
              data-testid="counter-search"
            />
          </div>

          <button
            onClick={() => setFoundModal(true)}
            className="inline-flex items-center gap-1 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-xs"
          >
            <Plus className="w-4 h-4" /> Found Item
          </button>

          <div className="flex items-center gap-1 flex-wrap">
            {[
              { id: "all", label: "All" },
              { id: "no_count", label: "No Count" },
              { id: "over_short", label: "Over / Short" },
              { id: "matched", label: "Matched" },
              { id: "found", label: "Found" },
              { id: "locked", label: "Locked" },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`rounded-sm px-3 py-1.5 text-xs ${
                  filter === f.id ? "bg-zinc-900 text-white" : "bg-zinc-100 hover:bg-zinc-200 text-zinc-800"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="data-table text-xs sm:text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-sm">
                <tr>
                  <th className="w-[110px] sm:w-auto">Code / Tonality</th>
                  <th className="hidden md:table-cell">Description</th>
                  <th className="hidden sm:table-cell">WH</th>
                  <th className="hidden lg:table-cell">Size</th>
                  <th className="hidden lg:table-cell">Location</th>
                  <th className="hidden sm:table-cell text-right">pcs/box</th>
                  <th className="hidden xl:table-cell text-right">System</th>
                  <th className="text-right">Your Count</th>
                  <th>Status</th>
                  <th className="hidden md:table-cell text-right">Attempts</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {items.map((it) => {
                  const rowCls =
                    it.ui_status === "matched"
                      ? "row-matched"
                      : it.ui_status === "recount_required"
                      ? "row-recount"
                      : it.ui_status === "locked"
                      ? "row-locked"
                      : "";

                  return (
                    <tr key={it.id} className={rowCls} data-testid={`counter-row-${it.id}`}>
                      <td className="mono font-medium leading-tight">
                        <div className="max-w-[105px] sm:max-w-none truncate">{it.item_code || "—"}</div>
                        <div className="text-[11px] text-zinc-600 max-w-[105px] sm:max-w-none truncate">
                          {it.tonality || "—"}
                        </div>
                        {it.source === "counter_added" && (
                          <div className="mt-0.5 inline-flex rounded-sm bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-800">
                            FOUND
                          </div>
                        )}
                      </td>

                      <td className="hidden md:table-cell min-w-[180px]">{it.item_description || "—"}</td>
                      <td className="mono hidden sm:table-cell">{it.warehouse_code || "—"}</td>
                      <td className="mono hidden lg:table-cell">{it.size || "—"}</td>
                      <td className="mono hidden lg:table-cell">{it.location || "—"}</td>
                      <td className="text-right mono hidden sm:table-cell">{it.pcs_per_box ?? "—"}</td>
                      <td className="text-right mono hidden xl:table-cell">{it.system_count ?? 0}</td>

                      <td className="text-right mono font-semibold">
                        {it.my_latest_count ?? "—"}
                        {it.pending_sync && (
                          <div className="text-[10px] text-amber-700 uppercase tracking-wider">queued</div>
                        )}
                      </td>

                      <td>
                        <CounterStatus item={it} isCutoff={isCutoff} />
                      </td>

                      <td className="text-right mono text-xs text-zinc-600 hidden md:table-cell">
                        {it.attempts_used ?? 0}/5
                      </td>

                      <td className="text-right">
                        {it.can_submit ? (
                          <button
                            onClick={() => openCountModal(it)}
                            className="inline-flex items-center gap-1 rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white px-3 py-1.5 text-xs"
                            data-testid={`counter-submit-${it.id}`}
                          >
                            <PaperPlaneRight className="w-3.5 h-3.5" />
                            {it.my_latest_count != null ? "Edit" : "Count"}
                          </button>
                        ) : (
                          <span className="text-xs text-zinc-500 italic">
                            {isCutoff ? "cut off" : "locked"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center py-10 text-zinc-500">
                      No items match your filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-zinc-950/60 flex items-center justify-center z-50 p-4"
          onClick={closeCountModal}
          data-testid="counter-submit-dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm border border-zinc-300 w-full max-w-lg max-h-[92vh] overflow-y-auto p-5"
          >
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">Count submission</div>
            <div className="font-display text-xl font-bold text-zinc-900 mt-1">{selected.item_code || "—"}</div>
            <div className="text-sm text-zinc-600">{selected.item_description || "—"}</div>
            <div className="mono text-xs text-zinc-500 mt-1">
              WH: {selected.warehouse_code || "—"} · TON: {selected.tonality || "—"} · LOC: {selected.location || "—"}
            </div>
            <div className="mono text-xs text-zinc-600 mt-1">
              System count: <span className="text-zinc-900">{selected.system_count ?? 0}</span>
            </div>

            <label className="block mt-5 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Warehouse Partner Name
            </label>
            <input
              type="text"
              list="partner-name-options"
              value={partnerVal}
              onChange={(e) => setPartnerVal(e.target.value)}
              maxLength={200}
              className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Choose or type the warehouse representative"
              data-testid="counter-partner-input"
            />
            <datalist id="partner-name-options">
              {partnerNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>

            <label className="block mt-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Current Location
            </label>
            <input
              type="text"
              value={locationVal}
              onChange={(e) => setLocationVal(e.target.value)}
              maxLength={300}
              className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Actual rack, alley, bin, or item location"
              data-testid="counter-location-input"
            />

            <div className="mt-4">
              <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
                Count History
              </div>

              <div className="mt-1 rounded-sm border border-zinc-200 bg-zinc-50 max-h-44 overflow-y-auto">
                {historyLoading ? (
                  <div className="px-3 py-4 text-sm text-zinc-500">
                    Loading history…
                  </div>
                ) : countHistory.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-zinc-500">
                    No submitted counts yet.
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-200">
                    {[...countHistory].reverse().map((entry) => (
                      <div key={entry.id} className="px-3 py-2 text-xs">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-zinc-900">
                            Attempt {entry.attempt_number}
                          </span>
                          <span className="font-mono text-base font-semibold text-zinc-900">
                            {entry.counted_quantity}
                          </span>
                        </div>

                        <div className="mt-1 text-zinc-600">
                          Partner: {entry.warehouse_partner_name || "—"}
                        </div>
                        <div className="text-zinc-600">
                          Location: {entry.submitted_location || "—"}
                        </div>

                        {entry.remarks && (
                          <div className="mt-1 text-zinc-600">
                            Remarks: {entry.remarks}
                          </div>
                        )}

                        <div className="mt-1 text-[11px] text-zinc-500">
                          {entry.submitted_at
                            ? new Date(entry.submitted_at).toLocaleString()
                            : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <label className="block mt-5 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Your physical count
            </label>
            <input
              autoFocus
              type="number"
              min={0}
              step={1}
              value={submitVal}
              onChange={(e) => setSubmitVal(e.target.value)}
              className="w-full mt-1 rounded-sm border-2 border-zinc-300 px-4 py-4 text-4xl font-mono text-center focus:border-indigo-500 focus:outline-none"
              placeholder="0"
              data-testid="counter-submit-input"
            />

            <label className="block mt-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Remarks
            </label>
            <textarea
              rows={2}
              value={remarksVal}
              onChange={(e) => setRemarksVal(e.target.value)}
              maxLength={500}
              className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Optional remarks"
              data-testid="counter-remarks-input"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={closeCountModal}
                className="rounded-sm border border-zinc-300 hover:bg-zinc-100 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={submitting}
                onClick={submit}
                className="rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-60 text-white px-4 py-2 text-sm inline-flex items-center gap-2"
                data-testid="counter-submit-confirm"
              >
                <PaperPlaneRight className="w-4 h-4" /> {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {foundModal && (
        <div
          className="fixed inset-0 bg-zinc-950/60 flex items-center justify-center z-50 p-4"
          onClick={() => !foundSaving && setFoundModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm border border-zinc-300 w-full max-w-lg p-5"
          >
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">Found item</div>
            <h3 className="font-display text-xl font-bold text-zinc-900 mt-1">Add item not in masterlist</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {[
                ["warehouse_code", "Warehouse"],
                ["item_code", "Item Code"],
                ["item_description", "Description"],
                ["size", "Size"],
                ["location", "Location"],
                ["tonality", "Tonality"],
                ["pcs_per_box", "pcs/box"],
                ["price", "Price"],
                ["counted_quantity", "Quantity Found"],
                ["alley_supervisor", "Alley Supervisor"],
              ].map(([key, label]) => (
                <input
                  key={key}
                  type={["pcs_per_box", "price", "counted_quantity"].includes(key) ? "number" : "text"}
                  className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
                  placeholder={label}
                  value={foundForm[key]}
                  onChange={(e) => setFoundForm({ ...foundForm, [key]: e.target.value })}
                />
              ))}
            </div>

            <textarea
              rows={2}
              className="w-full mt-2 rounded-sm border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Remarks"
              value={foundForm.remarks}
              onChange={(e) => setFoundForm({ ...foundForm, remarks: e.target.value })}
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setFoundModal(false)}
                className="rounded-sm border border-zinc-300 hover:bg-zinc-100 px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                disabled={foundSaving}
                onClick={addFoundItem}
                className="rounded-sm bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white px-4 py-2 text-sm"
              >
                {foundSaving ? "Saving…" : "Save Found Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
